import Select, { type SelectSize } from '@/components/ui/Select'

/** Every quarter hour of the day as "HH:mm" (24h, what the API takes). */
const QUARTER_HOURS = Array.from({ length: 96 }, (_, i) => {
  const h = String(Math.floor(i / 4)).padStart(2, '0')
  const m = String((i % 4) * 15).padStart(2, '0')
  return `${h}:${m}`
})

function label(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`
}

/**
 * A time picked from a list ("9:00 am", "9:15 am"…) instead of the browser's time field, whose
 * hour / minute / AM-PM segments read like seconds and reject a half-typed value. Value is "HH:mm",
 * or "" for no time when `emptyLabel` is given.
 */
export default function TimeSelect({
  value,
  onChange,
  emptyLabel,
  disabled,
  size,
  className,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (v: string) => void
  /** Offer a blank choice with this label (e.g. "No time"); omit when a time is required. */
  emptyLabel?: string
  disabled?: boolean
  size?: SelectSize
  className?: string
  'aria-label'?: string
}) {
  // Keep an off-grid stored time (e.g. 11:07) selectable rather than silently showing another.
  const options = value && !QUARTER_HOURS.includes(value) ? [...QUARTER_HOURS, value].sort() : QUARTER_HOURS
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} size={size} className={className} aria-label={ariaLabel}>
      {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
      {options.map((t) => (
        <option key={t} value={t}>
          {label(t)}
        </option>
      ))}
    </Select>
  )
}
